<template>
    <PageHeader :crumbs="[{ label: $t('portal.tickets.title'), to: { name: 'admin.tickets' } }, { label: ticket.subject || '...' }]" />

    <div class="tw-p-2 tw-space-y-4" v-if="!loading">
        <!-- Header -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-5">
            <div class="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 class="tw-text-xl tw-font-bold tw-text-gray-800">{{ ticket.subject }}</h1>
                    <p class="tw-text-sm tw-text-gray-400 tw-mt-1">
                        {{ ticket.customer_name }} · {{ fmt.datetime(ticket.created_at) }}
                    </p>
                </div>
                <div class="tw-flex tw-items-center tw-gap-2">
                    <TicketStatusBadge :status="ticket.status" />
                    <div class="tw-w-[150px]">
                        <FormField v-model="statusModel" type="select" :options="statusOptions" hide-errors @update:modelValue="changeStatus" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Conversation -->
        <div class="tw-bg-white tw-rounded tw-border tw-border-b-2 tw-border-solid tw-p-5">
            <TicketThread :messages="ticket.messages || []" :mine-is-admin="true" />

            <div v-if="ticket.status !== 'closed'" class="tw-mt-5 tw-pt-4 tw-border-t tw-border-solid">
                <FormField v-model="reply" type="textarea" :placeholder="$t('portal.tickets.reply-placeholder')" hide-errors />
                <div class="tw-flex tw-justify-end tw-mt-2">
                    <a-button :disabled="sending || !reply.trim()" :loading="sending" left-icon="solar:plain-bold-duotone" @click="sendReply">
                        {{ $t('portal.tickets.send-reply') }}
                    </a-button>
                </div>
            </div>
            <p v-else class="tw-mt-5 tw-pt-4 tw-border-t tw-border-solid tw-text-center tw-text-sm tw-text-gray-400">
                {{ $t('portal.tickets.closed-note') }}
            </p>
        </div>
    </div>

    <div v-else class="tw-p-8 tw-text-center tw-text-gray-400"><icon icon="line-md:loading-twotone-loop" class="tw-text-3xl" /></div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { show, reply as replyApi, updateStatus } from '@/api/tickets';
import PageHeader from '@/components/PageHeader.vue';
import FormField from '@/components/form/FormField';
import TicketThread from '@/components/TicketThread.vue';
import TicketStatusBadge from './TicketStatusBadge.vue';
import { useFormat } from '@/composables/useFormat';
import useAlert from '@/composables/useAlert';

const route = useRoute();
const i18n = useI18n();
const fmt = useFormat();
const id = route.params.id;

const ticket = ref({ subject: '', messages: [] });
const loading = ref(true);
const reply = ref('');
const sending = ref(false);
const statusModel = ref('open');

const statusOptions = computed(() => [
    { value: 'open', label: i18n.t('portal.tickets.status-open') },
    { value: 'answered', label: i18n.t('portal.tickets.status-answered') },
    { value: 'closed', label: i18n.t('portal.tickets.status-closed') },
]);

watch(ticket, (t) => { statusModel.value = t.status; });

async function load() {
    loading.value = true;
    try {
        const { data } = await show(id);
        ticket.value = data.ticket;
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

async function sendReply() {
    sending.value = true;
    try {
        const { data } = await replyApi(id, { message: reply.value });
        ticket.value = data.ticket;
        reply.value = '';
        useAlert(i18n.t('portal.tickets.reply-sent'));
    } catch (e) { /* interceptor */ } finally {
        sending.value = false;
    }
}

async function changeStatus(value) {
    if (value === ticket.value.status) return;
    try {
        const { data } = await updateStatus(id, value);
        ticket.value = { ...ticket.value, status: data.ticket.status };
    } catch (e) { /* interceptor */ }
}

load();
</script>
